import { CAPABILITY_LEVEL_DESCRIPTORS, CAPABILITY_LEVELS } from '@iculogbook/shared';
import type { CapabilityLevel } from '@iculogbook/shared';
import { useState } from 'react';

import { Button, Dialog, SegmentedControl } from '../../components/index.ts';
import { InfoIcon } from '../../layout/icons.tsx';

const SEGMENTS = CAPABILITY_LEVELS.map((level) => ({
  value: String(level),
  label: String(level),
  srLabel: `Capability level ${String(level)}`,
}));

type CapabilityLevelFieldProps = {
  value: CapabilityLevel | null;
  onChange: (value: CapabilityLevel) => void;
  error?: string | undefined;
};

/**
 * The FICM 1–4 capability scale. Never "supervision level" — see CLAUDE.md.
 *
 * The info button shows all four descriptors verbatim from the curriculum,
 * because a trainee deciding between 2 and 3 needs the wording in front of
 * them, and a paraphrase would be noticed by anyone who knows it.
 */
export function CapabilityLevelField({ value, onChange, error }: CapabilityLevelFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <span className="text-sm font-medium text-text">Capability level</span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="What the capability levels mean"
          className="grid size-touch place-items-center rounded-md text-muted hover:text-text"
        >
          <InfoIcon className="size-5" />
        </button>
      </div>

      <SegmentedControl
        label="Capability level"
        segments={SEGMENTS}
        value={value === null ? null : String(value)}
        onChange={(next) => onChange(Number(next) as CapabilityLevel)}
        className="h-14"
      />

      {error === undefined ? null : (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Capability levels"
        description="The FICM scale, as written in the ICM Curriculum 2021."
        footer={
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Close
          </Button>
        }
      >
        <dl className="mt-2 flex flex-col gap-3">
          {CAPABILITY_LEVELS.map((level) => (
            <div key={level} className="flex gap-3">
              <dt className="w-5 shrink-0 font-strong text-accent">{level}</dt>
              <dd className="text-sm text-muted">{CAPABILITY_LEVEL_DESCRIPTORS[level]}</dd>
            </div>
          ))}
        </dl>
      </Dialog>
    </div>
  );
}
